import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import { ReleaseNote } from '../../utils/api';
import { useFormatInstant } from '../../hooks/useClinicTimezone';
import { INSTANT_FORMATS } from '../../utils/dateUtils';

interface ReleaseNotesDialogProps {
  open: boolean;
  // Oldest first, ending on the newest: a user who missed releases steps through each one.
  releaseNotes: ReleaseNote[];
  onClose: () => void;
  onMarkSeen: () => void;
  loading?: boolean;
  error?: string | null;
}

const ReleaseNotesDialog: React.FC<ReleaseNotesDialogProps> = ({
  open,
  releaseNotes,
  onClose,
  onMarkSeen,
  loading = false,
  error = null,
}) => {
  // ReleaseNote.createdAt is an INSTANT (`@default(now())` — DATE-KINDS.md 1.4),
  // so it is rendered, never date-parted. The clinic's zone keeps one published
  // date for the whole practice; in the browser's, a note cut at 23:30 Eastern
  // dates itself a day forward for anyone further east.
  const formatInstant = useFormatInstant();
  const [index, setIndex] = useState(0);
  useEffect(() => setIndex(0), [releaseNotes]);

  const releaseNote = releaseNotes[Math.min(index, releaseNotes.length - 1)] ?? null;
  const total = releaseNotes.length;
  const isLast = index >= total - 1;

  // Finishing or skipping both mark everything up to the newest as seen.
  const handleGotIt = () => {
    onMarkSeen();
    onClose();
  };

  if (!releaseNote && !loading && !error) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '80vh',
        },
      }}
    >
      {loading && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 200,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {error && (
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        </DialogContent>
      )}

      {releaseNote && !loading && (
        <>
          <DialogTitle
            sx={{
              pb: 1,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Typography variant="h5" component="div" gutterBottom>
              {releaseNote.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Version {releaseNote.version} •{' '}
              {formatInstant(releaseNote.createdAt, INSTANT_FORMATS.dateNumeric)}
              {total > 1 && ` • Update ${index + 1} of ${total}`}
            </Typography>
          </DialogTitle>

          {/* keyed so Next and Back start each note at its top */}
          <DialogContent key={releaseNote.id} sx={{ pt: 3, pb: 2 }}>
            <Box
              sx={{
                '& h1, & h2, & h3, & h4, & h5, & h6': {
                  color: 'primary.main',
                  mt: 2,
                  mb: 1,
                },
                '& h1': {
                  fontSize: '1.75rem',
                  fontWeight: 600,
                },
                '& h2': {
                  fontSize: '1.5rem',
                  fontWeight: 600,
                },
                '& h3': {
                  fontSize: '1.25rem',
                  fontWeight: 600,
                },
                '& p': {
                  mb: 1.5,
                  lineHeight: 1.6,
                },
                '& ul, & ol': {
                  pl: 3,
                  mb: 1.5,
                },
                '& li': {
                  mb: 0.5,
                },
                '& code': {
                  backgroundColor: 'grey.100',
                  color: 'error.main',
                  px: 0.5,
                  py: 0.25,
                  borderRadius: 1,
                  fontSize: '0.875rem',
                  fontFamily: 'monospace',
                },
                '& pre': {
                  backgroundColor: 'grey.100',
                  p: 2,
                  borderRadius: 1,
                  overflow: 'auto',
                  '& code': {
                    backgroundColor: 'transparent',
                    color: 'inherit',
                    px: 0,
                    py: 0,
                  },
                },
                '& blockquote': {
                  borderLeft: 4,
                  borderColor: 'primary.main',
                  pl: 2,
                  ml: 0,
                  fontStyle: 'italic',
                  color: 'text.secondary',
                },
                '& a': {
                  color: 'primary.main',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                },
                '& strong': {
                  fontWeight: 600,
                },
                '& table': {
                  width: '100%',
                  borderCollapse: 'collapse',
                  mt: 1,
                  mb: 2,
                },
                '& th, & td': {
                  border: 1,
                  borderColor: 'divider',
                  p: 1,
                  textAlign: 'left',
                },
                '& th': {
                  backgroundColor: 'grey.50',
                  fontWeight: 600,
                },
              }}
            >
              <ReactMarkdown>{releaseNote.body}</ReactMarkdown>
            </Box>
          </DialogContent>

          <DialogActions sx={{ p: 3, pt: 1 }}>
            {!isLast && (
              <Button onClick={handleGotIt} sx={{ mr: 'auto' }}>
                Skip the rest
              </Button>
            )}
            {index > 0 && (
              <Button onClick={() => setIndex(index - 1)} size="large">
                Back
              </Button>
            )}
            <Button
              onClick={isLast ? handleGotIt : () => setIndex(index + 1)}
              variant="contained"
              size="large"
              sx={{ minWidth: 120 }}
            >
              {isLast ? 'Got it!' : 'Next'}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default ReleaseNotesDialog;
